import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTrigger
} from '@growdash/design-system/atoms';
import {
  ConfirmDeleteDialog,
  DataTable,
  RHFInput,
  RHFMultiSelect,
  RHFSelect,
  SingleDatePicker
} from '@growdash/design-system/molecules';
import { yupResolver } from '@hookform/resolvers/yup';
import { ManagementService } from '@modules/management/api';
import CoFundingFilters from '@modules/operations/components/co-funding-filters.tsx';
import { RootState } from '@store/index.ts';
import { IBrand } from '@utils/interfaces';
import { addDays, endOfToday, format } from 'date-fns';
import { Check, Loader2, Plus, Trash2 } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';

interface BranchesInterface {
  id: number;
  name: string;
  isAvailable: boolean;
}

interface CoFundingData {
  id: number;
  startDate: string;
  endDate: string;
  channel: string;
  brandName: string;
  locationNames: string[];
  type: string;
  value: number;
}

export interface CofundingFilterFormValues {
  channels: string[];
  brands: number[];
  branches: number[];
}

export interface Branch {
  id: number;
  name: string;
  brands: number[];
}

export interface Brand {
  id: number;
  name: string;
}

interface CofundingType {
  label: string;
  value: string;
}

const CofundingTypes: CofundingType[] = [
  { label: 'Percentage', value: 'percentage' },
  { label: 'Amount per order', value: 'amount-per-order' }
];

interface FormValues {
  channel: string;
  brand: string;
  branches: number[];
  type: string;
  value: number;
  sDate: Date;
  eDate: Date;
}

const EventSchema = Yup.object({
  channel: Yup.string().required('Channel is required'),
  brand: Yup.string().required('Brand is required'),
  branches: Yup.array().min(1, 'At least 1 branch is required.').default([]),

  type: Yup.string().required('Type is required'),
  value: Yup.number()
    .required('Value is required')
    .min(1)
    .when('type', {
      is: 'percentage',
      then: schema => schema.max(100, 'Value must be less than 100%')
    }),
  sDate: Yup.date().required('Start date is required'),
  eDate: Yup.date().required('End date is required')
});

export default function CoFundingTab() {
  const { BRANDS, CHANNELS, LOCATIONS } = useSelector(
    (state: RootState) => state.filter
  );

  const { enqueueSnackbar } = useSnackbar();
  const [tableDataLoading, setTableDataLoading] = useState<boolean>(false);

  const [coFundingBranches, setCoFundingBranches] = useState<
    BranchesInterface[]
  >([]);
  const [tableData, setTableData] = useState<CoFundingData[]>([]);
  const [openDeleteConform, setOpenDeleteConform] = useState<boolean>(false);
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [creationSuccess, setCreationSuccess] = useState<boolean>(false);

  const methods = useForm<FormValues>({
    resolver: yupResolver(EventSchema),
    defaultValues: {
      channel: '',
      brand: '',
      branches: [],
      type: '',
      value: 0,
      sDate: endOfToday(),
      eDate: addDays(endOfToday(), 24)
    }
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
    reset,
    setValue
  } = methods;

  // Form values for creating co-funding
  const channel = useWatch({ control, name: 'channel' });
  const brand = useWatch({ control, name: 'brand' });
  const eDate = useWatch({ control, name: 'eDate' });
  const sDate = useWatch({ control, name: 'sDate' });

  // Fetch co-funding branches based on selected channel and brand on create form.
  const [branchesLoading, setBranchesLoading] = useState<boolean>(false);

  const getCoFundingBranches = useCallback(
    async (
      startDate: string,
      endDate: string,
      channelId: string,
      brandId: number,
      page: number,
      take: number
    ) => {
      try {
        setBranchesLoading(true);
        const coFundingBranchesData =
          await ManagementService?.fetchCoFundingBranches(
            startDate,
            endDate,
            channelId,
            brandId,
            page,
            take
          );
        setCoFundingBranches(coFundingBranchesData.branches);
      } catch (error) {
        enqueueSnackbar(error?.message, { variant: 'error' });
      } finally {
        setBranchesLoading(false);
      }
    },
    [enqueueSnackbar]
  );

  useEffect(() => {
    if (channel && brand && eDate) {
      getCoFundingBranches(
        sDate.toLocaleDateString('en-CA'),
        eDate.toLocaleDateString('en-CA'),
        channel,
        Number(brand),
        page,
        rowsPerPage
      );
    }
  }, [channel, brand, eDate, sDate, page, rowsPerPage, getCoFundingBranches]);

  const fetchTableData = useCallback(
    async (
      pageNumber: number = page,
      take: number = rowsPerPage,
      channelIds?: string[],
      brandIds?: number[],
      branchIds?: number[]
    ) => {
      try {
        setTableDataLoading(true);
        const response = await ManagementService?.fetchCoFunding(
          pageNumber,
          take,
          channelIds,
          brandIds,
          branchIds
        );
        setTableData(response.campaigns);
      } catch (error) {
        enqueueSnackbar(error?.message, { variant: 'error' });
      } finally {
        setTableDataLoading(false);
      }
    },
    [enqueueSnackbar, page, rowsPerPage]
  );

  useEffect(() => {
    fetchTableData();
  }, [fetchTableData]);

  useEffect(() => {
    setValue('branches', []);
    setCoFundingBranches([]);
  }, [channel, brand, setValue]);

  const handleFilterChange = (filters: CofundingFilterFormValues) => {
    fetchTableData(
      page,
      rowsPerPage,
      filters.channels,
      filters.brands,
      filters.branches
    );
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchTableData(newPage, rowsPerPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchTableData(page, newRowsPerPage);
  };

  const onSubmit = async (data: FormValues) => {
    try {
      const eventData = {
        startDate: data.sDate.toLocaleDateString('en-CA'),
        endDate: data.eDate.toLocaleDateString('en-CA'),
        brandId: Number(data.brand),
        branchIds: data.branches,
        channel: data.channel,
        type: data.type === 'percentage' ? 'percentage' : 'amount-per-order',
        value: data.value
      };
      await ManagementService?.createCoFunding(eventData);
      enqueueSnackbar('Successfully added new entry');
      reset();
      handleFilterChange({
        channels: [],
        brands: [],
        branches: []
      });
      setCreationSuccess(true);
    } catch (error) {
      enqueueSnackbar(error?.message || 'Something went wrong', {
        variant: 'error'
      });
    }
  };

  const handleDelete = async () => {
    try {
      if (selectedRowId) {
        await ManagementService?.deleteCoFunding(selectedRowId);
        enqueueSnackbar('Successfully deleted entry');
        handleFilterChange({
          channels: [],
          brands: [],
          branches: []
        });
      }
    } catch (error) {
      setOpenDeleteConform(false);
      enqueueSnackbar(error?.message || 'Failed to delete', {
        variant: 'error'
      });
    } finally {
      setOpenDeleteConform(false);
    }
  };

  const tableLabels = [
    {
      id: 'startDate',
      header: 'Start Date',
      accessorKey: 'startDate',
      cell: ({ row }: any) => {
        const startDate = row?.getValue('startDate');
        return (
          <div>
            {startDate ? format(new Date(startDate), 'dd/MM/yyyy') : '--'}
          </div>
        );
      }
    },
    {
      id: 'endDate',
      header: 'End Date',
      accessorKey: 'endDate',
      cell: ({ row }: any) => {
        const endDate = row?.getValue('endDate');
        return (
          <div>{endDate ? format(new Date(endDate), 'dd/MM/yyyy') : '--'}</div>
        );
      }
    },
    {
      id: 'channel',
      header: 'Channel',
      accessorKey: 'channel',
      cell: ({ row }: any) => {
        const channel = row?.getValue('channel');
        return <div className='capitalize'>{channel || '--'}</div>;
      }
    },
    {
      id: 'brandName',
      header: 'Brand',
      accessorKey: 'brandName',
      cell: ({ row }: any) => {
        const brandName = row?.getValue('brandName');
        return <div>{brandName || '--'}</div>;
      }
    },
    {
      id: 'locationNames',
      header: 'Branch',
      accessorKey: 'locationNames',
      cell: ({ row }: any) => {
        const locations = row?.getValue('locationNames');
        return <div>{locations?.length ? locations.join(', ') : '--'}</div>;
      }
    },
    {
      id: 'type',
      header: 'Type',
      accessorKey: 'type',
      cell: ({ row }: any) => {
        const type = row?.getValue('type');
        return (
          <div>{CofundingTypes.find(c => c.value === type)?.label || '--'}</div>
        );
      }
    },
    {
      id: 'value',
      header: 'Value',
      accessorKey: 'value',
      cell: ({ row }: any) => {
        const value = row?.getValue('value');
        return <div>{value !== undefined ? `${value} %` : '--'}</div>;
      }
    },
    {
      id: 'action',
      header: 'Action',
      accessorKey: 'action',
      cell: ({ row }: any) => {
        const id = row?.original?.id;
        return (
          <>
            <div
              className='text-center cursor-pointer bg-[#FAE1E1] bg-opacity-25 rounded-md 
            p-1.5 max-w-min'
              onClick={() => {
                handleDeleteAction(id);
              }}
            >
              <Trash2 size={20} className='text-[#C92222] ' />
            </div>
          </>
        );
      }
    }
  ];

  const handleDeleteAction = (id: number) => {
    setOpenDeleteConform(true);
    setSelectedRowId(id);
  };
  return (
    <FormProvider {...methods}>
      {/* <Dialog 
      onOpenChange={isOpen => {
        if (!isOpen) {
          setCreationSuccess(false);
        }
      }}
      
      open={creationSuccess}>
        <DialogContent>
          <DialogHeader>Entry Successfully Created</DialogHeader>
          <DialogDescription>
            Your entry has been successfully created.
          </DialogDescription>
          <DialogFooter>
            <Button onClick={() => setOpenDeleteConform(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog> */}
      <Dialog
        key={creationSuccess ? 'success' : 'none'}
        onOpenChange={isOpen => {
          if (!isOpen) {
            reset();
            setCreationSuccess(prev => !prev);
          }
        }}
      >
        <DialogTrigger asChild>
          <div className='flex justify-end w-min ml-auto'>
            <Button className=''>
              Create New
              <Plus size={24} fontWeight={500} />
            </Button>
          </div>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>Create Co-Funding</DialogHeader>
          {/* TODO: Check Mui's LocalizationProvider's effect, find alternative if needed. */}
          {/* <LocalizationProvider dateAdapter={AdapterDateFns}> */}
          <div className='flex justify-between gap-2'>
            <div className='basis-full'>
              <SingleDatePicker
                control={control}
                name='sDate'
                label='Start date'
              />
            </div>
            <div className='basis-full'>
              <SingleDatePicker
                control={control}
                name='eDate'
                label='End date'
                startDate={addDays(sDate, 1)}
              />
            </div>
          </div>

          <RHFSelect
            name='channel'
            label='Channel'
            placeholder='Select Channel'
            options={CHANNELS.map((option: IBrand) => ({
              label: option?.name,
              value: option?.name
            }))}
          />

          <RHFSelect
            name='brand'
            label='Brand'
            options={BRANDS.map((option: IBrand) => ({
              label: option?.name,
              value: option?.id.toString()
            }))}
            placeholder='Select a Brand'
          />

          <div className='flex-1 min-w-200 w-200'>
            <RHFMultiSelect
              name='branches'
              className={`${branchesLoading ? 'animate-pulse' : ''}`}
              placeholder={
                branchesLoading ? 'Branches Loading...' : 'Select Branches'
              }
              disabled={branchesLoading}
              options={coFundingBranches.map(branch => ({
                label: branch.name,
                value: branch.id.toString()
              }))}
              maxChips={2}
            />
          </div>

          <RHFSelect
            name='type'
            label='Type'
            placeholder='Select Co-funding Type'
            options={CofundingTypes}
          />

          <RHFInput type='number' name='value' label='Value' />
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting || !isValid}
          >
            Create
            {isSubmitting ? <Loader2 className='animate-spin' /> : <Check />}
          </Button>
        </DialogContent>
      </Dialog>
      <div className='mt-6'>
        <Card className='pt-4'>
          <CardContent>
            <CoFundingFilters
              channels={CHANNELS}
              brands={BRANDS}
              branches={LOCATIONS}
              onFilterChange={handleFilterChange}
              defaultValues={{
                channels: [],
                brands: [],
                branches: []
              }}
            />
            <div className='mt-4'>
              <DataTable
                isLoading={tableDataLoading}
                columns={tableLabels}
                data={tableData}
                isPaginationActive
                pagination={{
                  pageSize: rowsPerPage,
                  pageIndex: page,
                  pageCount: Math.ceil((tableData?.length ?? 0) / rowsPerPage),
                  onPageChange: handlePageChange,
                  onRowsPerPageChange: handleRowsPerPageChange
                }}
              />
            </div>
          </CardContent>
        </Card>
      </div>
      <ConfirmDeleteDialog
        open={openDeleteConform}
        onClose={() => setOpenDeleteConform(false)}
        title='Delete Co-Funding?'
        content='This action can not be undone.
        This will permanently delete your Co-Funding campaign'
        action={
          <Button color='error' onClick={() => handleDelete()}>
            Delete
          </Button>
        }
      />
    </FormProvider>
  );
}
